import { darken, alpha, type Theme } from '@biome-design-system/styles';
import { fromTheme as kyruusFromTheme } from '@kyruus/ui-theme';
import { getOverrides, type ThemeContext } from '../utils';

/**
 * Generates MUI Button component style overrides using theme values.
 * @param {object} kyruusTheme - The Kyruus theme object
 * @param {object} biomeTheme - The Biome theme object (which is a MUI theme)
 * @returns {object} MUI Button style overrides for the theme.
 *
 * The returned object customizes the appearance of MUI Buttons, including
 * background color, border, font, and disabled/focus states, based on the
 * provided theme values.
 */

export const buildButton = (
  kyruusTheme: Record<string, unknown>,
  biomeTheme: Theme
) => {
  // wrap fromTheme to simplify the code below
  const fromTheme = (themeKey: string) =>
    kyruusFromTheme(themeKey)({ theme: kyruusTheme });

  const buttonColor = fromTheme('color_button');
  const borderRadius = fromTheme('border_radius');
  const borderWidth = fromTheme('border_width');
  const fontFace = fromTheme('font_face');
  const fontSize = fromTheme('font_size');
  const fontWeight = fromTheme('font_weight');

  const { root, contained, outlinedPrimary, text } =
    biomeTheme.components?.MuiButton?.styleOverrides || {};
  return {
    styleOverrides: {
      root: (context: ThemeContext) => ({
        ...getOverrides(root, context),
        borderWidth: borderWidth,
        borderRadius: borderRadius,
        fontFamily: fontFace,
        fontSize: fontSize,
        fontWeight: fontWeight
      }),
      contained: ({ theme: currentTheme, ownerState }: ThemeContext) => ({
        ...getOverrides(contained, { theme: currentTheme, ownerState }),
        '&:not(:disabled, .disabled)': {
          color: currentTheme.palette.common.white,
          borderColor: buttonColor,
          backgroundColor: buttonColor,
          '&:hover, &:focus, &.hover, &.focus': {
            borderColor: darken(buttonColor, 0.2),
            backgroundColor: darken(buttonColor, 0.2)
          },
          '&:focus:not(:focus-visible):not(:hover)': {
            boxShadow: 'none',
            borderColor: buttonColor,
            backgroundColor: buttonColor
          },
          '&:focus-visible, &.focus': {
            borderColor: currentTheme.palette.common.white,
            boxShadow: `0 0 0 1px ${
              currentTheme.palette.common.white
            }, 0 0 0 3px ${darken(buttonColor, 0.2)}`
          },
          '&:active, &.active': {
            boxShadow: 'none',
            backgroundColor: darken(buttonColor, 0.4),
            borderColor: darken(buttonColor, 0.4)
          }
        }
      }),
      outlinedPrimary: ({ theme: currentTheme, ownerState }: ThemeContext) => ({
        ...getOverrides(outlinedPrimary, { theme: currentTheme, ownerState }),
        borderColor: buttonColor
      }),
      outlined: ({ theme: currentTheme }: ThemeContext) => ({
        '&.MuiButton-outlinedNeutral, &.MuiButton-outlinedDefault': {
          borderColor: currentTheme.palette.grey[600],
          '&:hover, &.hover': {
            backgroundColor: alpha(currentTheme.palette.grey[500], 0.1)
          },
          '&:focus:not(:focus-visible)': {
            outline: 'none',
            boxShadow: 'none'
          },
          '&:focus-visible, &.focus': {
            borderColor: currentTheme.palette.grey[900],
            boxShadow: `0 0 0 1px ${currentTheme.palette.grey[900]}`
          },
          '&:active, &.active': {
            boxShadow: 'none',
            backgroundColor: alpha(currentTheme.palette.grey[500], 0.2),
            borderColor: currentTheme.palette.grey[900]
          },
          '&:not(:disabled, .disabled)': {
            color: currentTheme.palette.common.black
          },
          '&:disabled, &.disabled': {
            boxShadow: 'none',
            borderColor: currentTheme.palette.grey[200],
            backgroundColor: 'transparent'
          }
        },
        '&:disabled, &.disabled': {
          backgroundColor: 'transparent',
          borderColor: currentTheme.palette.grey[200],
          boxShadow: 'none'
        }
      }),
      text: ({ theme: currentTheme, ownerState }: ThemeContext) => ({
        ...getOverrides(text, { theme: currentTheme, ownerState }),
        '&:not(:disabled, .disabled)': {
          color: buttonColor
        },
        '&:hover, &.hover': {
          backgroundColor: alpha(buttonColor, 0.1)
        },
        '&:active, &.active': {
          boxShadow: 'none',
          backgroundColor: alpha(buttonColor, 0.2)
        }
      })
    }
  };
};
